import React, { useState, useEffect } from 'react';
import api from '../services/api';

const BalanceAdjustmentModal = ({ account, onAdjust, onCancel }) => {
  const [adjustment, setAdjustment] = useState('');
  const [reason, setReason] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!adjustment || isNaN(parseFloat(adjustment))) {
      alert('Please enter a valid adjustment amount.');
      return;
    }
    onAdjust(parseFloat(adjustment), reason);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onCancel}>
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="p-4 border-b border-neutral-200">
            <h3 className="font-medium">Adjust Balance for {account.name}</h3>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Current Balance</label>
              <p className="font-mono text-lg">{parseFloat(account.balance).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</p>
            </div>
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Adjustment Amount *</label>
              <input
                type="number"
                step="0.01"
                value={adjustment}
                onChange={e => setAdjustment(e.target.value)}
                className="w-full h-9 px-2 text-sm border border-neutral-300 rounded"
                placeholder="e.g., -50.25 or 100"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Reason for Adjustment</label>
              <input
                type="text"
                value={reason}
                onChange={e => setReason(e.target.value)}
                className="w-full h-9 px-2 text-sm border border-neutral-300 rounded"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 p-4 bg-gray-50 border-t border-neutral-200">
            <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-neutral-600 bg-white border rounded-md">Cancel</button>
            <button type="submit" className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md">Apply Adjustment</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BankAccountFormModal = ({ account, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    accountType: 'checking',
    balance: 0,
    creditLimit: null,
    isPrimary: false,
  });

  useEffect(() => {
    if (account) {
      setFormData({
        name: account.name,
        accountType: account.accountType,
        balance: account.balance,
        creditLimit: account.creditLimit,
        isPrimary: account.isPrimary,
      });
    } else {
      setFormData({ name: '', accountType: 'checking', balance: 0, creditLimit: null, isPrimary: false });
    }
  }, [account]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onCancel}>
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="p-4 border-b border-neutral-200">
            <h3 className="font-medium">{account ? 'Edit Bank Account' : 'New Bank Account'}</h3>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Account Name *</label>
              <input type="text" value={formData.name} onChange={e => setFormData(f => ({ ...f, name: e.target.value }))} className="w-full h-9 px-2 text-sm border border-neutral-300 rounded" required />
            </div>
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Account Type *</label>
              <select value={formData.accountType} onChange={e => setFormData(f => ({ ...f, accountType: e.target.value }))} className="w-full h-9 px-2 text-sm border border-neutral-300 rounded bg-white" required>
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
                <option value="cash">Cash</option>
                <option value="credit_card">Credit Card</option>
              </select>
            </div>
            {formData.accountType === 'credit_card' && (
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Credit Limit</label>
                <input type="number" step="0.01" value={formData.creditLimit || ''} onChange={e => setFormData(f => ({ ...f, creditLimit: e.target.value ? parseFloat(e.target.value) : null }))} className="w-full h-9 px-2 text-sm border border-neutral-300 rounded" />
              </div>
            )}
            {!account && (
                 <div>
                    <label className="block text-xs text-neutral-500 mb-1">Starting Balance *</label>
                    <input type="number" step="0.01" value={formData.balance} onChange={e => setFormData(f => ({ ...f, balance: e.target.value }))} className="w-full h-9 px-2 text-sm border border-neutral-300 rounded" required />
                 </div>
            )}
            <div className="flex items-center">
              <input type="checkbox" id="is_primary" checked={formData.isPrimary} onChange={e => setFormData(f => ({ ...f, isPrimary: e.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <label htmlFor="is_primary" className="ml-2 block text-sm text-gray-900">Is Primary Account?</label>
            </div>
          </div>
          <div className="flex justify-end gap-2 p-4 bg-gray-50 border-t border-neutral-200">
            <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-neutral-600 bg-white border rounded-md">Cancel</button>
            <button type="submit" className="px-4 py-2 text-sm bg-neutral-800 text-white rounded-md">{account ? 'Save Changes' : 'Create Account'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BankAccountManager = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(null);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const data = await api.getBankAccounts();
      setAccounts(data);
    } catch (error) {
      console.error('Failed to fetch bank accounts:', error);
      alert('Could not load bank accounts.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleSave = async (formData) => {
    try {
      if (editingAccount) {
        await api.updateBankAccount(editingAccount.id, formData);
      } else {
        await api.createBankAccount(formData);
      }
      setShowForm(false);
      setEditingAccount(null);
      fetchAccounts();
    } catch (error) {
      console.error('Failed to save bank account:', error);
      alert('Error saving bank account.');
    }
  };

  const handleAdjustBalance = async (amount, reason) => {
    try {
      await api.adjustBankAccountBalance(showAdjustmentModal.id, { amount, reason });
      setShowAdjustmentModal(null);
      fetchAccounts();
    } catch (error) {
      console.error('Failed to adjust balance:', error);
      alert('Error adjusting balance.');
    }
  };

  if (loading) return <div>Loading bank accounts...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-bold">Manage Bank Accounts</h1>
        <button onClick={() => { setEditingAccount(null); setShowForm(true); }} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">
          + Add Account
        </button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
              <th className="p-3 font-medium">Name</th>
              <th className="p-3 font-medium">Type</th>
              <th className="p-3 font-medium text-right">Balance</th>
              <th className="p-3 font-medium text-right">Credit Limit</th>
              <th className="p-3 font-medium">Primary</th>
              <th className="p-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map(account => (
              <tr key={account.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                <td className="p-3">{account.name}</td>
                <td className="p-3 capitalize">{(account.accountType || '').replace('_', ' ')}</td>
                <td className={`p-3 font-mono text-right ${parseFloat(account.balance) < 0 ? 'text-red-600' : 'text-green-700'}`}>
                  {parseFloat(account.balance).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </td>
                <td className="p-3 font-mono text-right">
                  {account.creditLimit ? parseFloat(account.creditLimit).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '-'}
                </td>
                <td className="p-3">{account.isPrimary ? 'Yes' : 'No'}</td>
                <td className="p-3">
                  <div className="flex items-center gap-4 text-xs">
                    <button onClick={() => { setEditingAccount(account); setShowForm(true); }} className="text-blue-600 hover:underline">Edit</button>
                    <button onClick={() => setShowAdjustmentModal(account)} className="text-green-600 hover:underline">Adjust</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <BankAccountFormModal
          account={editingAccount}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingAccount(null);
          }}
        />
      )}

      {showAdjustmentModal && (
        <BalanceAdjustmentModal
          account={showAdjustmentModal}
          onAdjust={handleAdjustBalance}
          onCancel={() => setShowAdjustmentModal(null)}
        />
      )}
    </div>
  );
};

export default BankAccountManager;
